// Bulk-SMS limits, cost model and opt-out footer — shared by the API (resolver, gates, worker) and
// the admin web app (Compose/Review counters). Pure constants + tiny pure helpers, no I/O, so both
// sides compute the same cap, cost estimate and STOP footer without a round-trip.

/** Hard ceiling on distinct people in one batch. The live cap is lower until the pilot passes: the API
 *  clamps env `MARKETING_BULK_SMS_MAX_RECIPIENTS` (default {@link DEFAULT_EFFECTIVE_MAX_BULK_RECIPIENTS})
 *  to this value — see `getEffectiveMaxRecipients`. */
export const MAX_BULK_RECIPIENTS = 10000;

/** Effective per-batch cap when `MARKETING_BULK_SMS_MAX_RECIPIENTS` is unset (staged rollout). */
export const DEFAULT_EFFECTIVE_MAX_BULK_RECIPIENTS = 500;

/** A batch must finish within this many daily waves; a longer plan is blocked (shorten text / narrow list). */
export const MAX_BULK_WAVE_DAYS = 7;

/** Share of the salon's daily marketing limit (min of carrier cap and budget) bulk sends may use; the
 *  rest stays free for automations, and the carrier headroom for appointment reminders. */
export const BULK_CARRIER_SHARE = 0.8;

/** Carrier daily cap (segments) used until Twilio's campaign `rate_limits` are synced, by brand type.
 *  Conservative: Sole Proprietor brands are capped at 1,000/day by T-Mobile, the lowest standard tier
 *  at 2,000/day. */
export const FALLBACK_CARRIER_DAILY_CAP = {
  SOLE_PROPRIETOR: 1000,
  STANDARD: 2000,
  UNKNOWN: 1000,
} as const;

/** Sending pace (texts/minute) used until Twilio's campaign `rate_limits` are synced. */
export const FALLBACK_MESSAGES_PER_MINUTE = 60;

/** Bounds for the per-salon daily bulk text budget an agency admin may set ($5 – $500). */
export const BULK_SMS_DAILY_BUDGET_CENTS_MIN = 500;
export const BULK_SMS_DAILY_BUDGET_CENTS_MAX = 50000;

/** Anti-fatigue window: exclude anyone who received any marketing SMS (automation OR bulk) within this
 *  many hours. Appointment texts never count; consent + STOP remain the hard gates. */
export const BULK_SMS_FATIGUE_WINDOW_HOURS = 72;

/** Default per-salon daily marketing-SMS budget in cents ($50) when the salon has no
 *  `bulkSmsDailyBudgetCents` setting. Env-overridable via `MARKETING_BULK_SMS_DAILY_CAP_CENTS`. */
export const BULK_SMS_DAILY_CAP_CENTS_DEFAULT = 5000;

/** Conservative per-segment cost estimate in cents (Twilio + carrier fee). Estimate only — used for
 *  the Review screen's "$x of $50" and the atomic daily reservation, never billed. */
export const SMS_SEGMENT_COST_CENTS = 1.25;

/** Appended to a body that lacks an explicit STOP notice so every bulk text carries opt-out language
 *  (counted in segments/cost). */
export const BULK_SMS_OPT_OUT_FOOTER = 'Reply STOP to opt out';

/** Worker sends in chunks of this size; after each chunk it pauses long enough to hold the salon's
 *  carrier pace (texts/minute), never less than {@link BULK_SMS_MIN_CHUNK_PAUSE_MS}. */
export const BULK_SMS_CHUNK_SIZE = 25;
export const BULK_SMS_MIN_CHUNK_PAUSE_MS = 1000;

/** Highest pace the worker ever targets (texts/minute), whatever the carrier allows. Synced AT&T limits
 *  can reach 60,000/min; one number sending ~10/s is already far past what a salon blast needs. */
export const BULK_SMS_MAX_MESSAGES_PER_MINUTE = 600;

/** Highest pace the sending-plan ESTIMATE assumes (texts/minute). The worker sends a chunk's texts one
 *  after another (Twilio call + DB writes each) and then pauses, and shares a platform-wide concurrency
 *  limit, so it really manages ~100–120/min however fast the carrier allows — promising the carrier pace
 *  would turn a "1 day, about 2 min" plan into a late or 2-day send. */
export const BULK_SMS_PLANNING_MAX_MESSAGES_PER_MINUTE = 100;

/** The exclusion reasons a recipient can carry, mirroring the Prisma `MarketingBulkExclusionReason`
 *  enum (kept as a string union here so `@repo/shared` stays free of a `@repo/db` dependency). */
export const BULK_SMS_EXCLUSION_REASONS = [
  'OPT_OUT',
  'NO_CONSENT',
  'RECENTLY_MESSAGED',
  'NO_PHONE',
  'UNRESOLVED',
  'SMS_BLOCKED',
  'CANCELLED',
  'UNDELIVERABLE',
  'DUPLICATE_PHONE',
  'GIFT_CLOSED',
  'GIFT_SOLD_OUT',
  'GIFT_ALREADY_USED',
  'GIFT_OFFER_NOT_LIVE',
  'GIFT_NOT_ELIGIBLE',
  'SURVEY_CLOSED',
  'SURVEY_ALREADY_ANSWERED',
] as const;
export type BulkSmsExclusionReason = (typeof BULK_SMS_EXCLUSION_REASONS)[number];

/** Human labels for the Compose left-rail exclusion list and the Result screen. */
export const BULK_SMS_EXCLUSION_LABELS: Record<BulkSmsExclusionReason, string> = {
  OPT_OUT: 'Opted out · replied STOP',
  NO_CONSENT: 'No consent on file',
  RECENTLY_MESSAGED: 'Messaged in the last 3 days',
  NO_PHONE: 'No US/Canada phone number',
  UNRESOLVED: 'Still resolving',
  SMS_BLOCKED: 'SMS blocked for this salon',
  CANCELLED: 'Cancelled',
  UNDELIVERABLE: "Number can't receive texts (landline or invalid)",
  DUPLICATE_PHONE: 'Same phone as another person in this list',
  GIFT_CLOSED: 'The gift ended or was removed before their text went out',
  GIFT_SOLD_OUT: 'All vouchers were given before their text went out',
  GIFT_ALREADY_USED: 'Already used this voucher, or the salon took it back',
  GIFT_OFFER_NOT_LIVE: 'The New Client Offer was off when their text went out',
  GIFT_NOT_ELIGIBLE: "Can't get this gift: not a new guest, or won a prize",
  SURVEY_CLOSED: 'The survey was deleted before their text went out',
  SURVEY_ALREADY_ANSWERED: 'Already answered this survey',
};

/**
 * Exclusion reasons for ticked Clients rows that never become a recipient row (so no Prisma enum value):
 * a BLOCKED client, or a client deleted, merged or gone since being ticked. The detail lists them with the
 * live exclusions, so "of N you picked" minus "will receive" always equals the list below it.
 */
export const BULK_SMS_PICKED_CLIENT_EXCLUSION_REASONS = ['BLOCKED_CLIENT', 'NOT_IN_CLIENT_LIST'] as const;
export type BulkSmsPickedClientExclusionReason = (typeof BULK_SMS_PICKED_CLIENT_EXCLUSION_REASONS)[number];

export const BULK_SMS_PICKED_CLIENT_EXCLUSION_LABELS: Record<BulkSmsPickedClientExclusionReason, string> = {
  BLOCKED_CLIENT: 'Blocked client',
  NOT_IN_CLIENT_LIST: 'No longer in your client list',
};

/** True when a text tells the reader how to opt out ("reply STOP" / "text STOP"), not merely uses the word "stop". */
export function hasOptOutInstruction(body: string): boolean {
  return /\b(?:reply|text)\s+stop\b/i.test(body);
}

/**
 * Append the opt-out footer unless the body already carries an explicit opt-out instruction — the
 * phrasing "reply STOP" or "text STOP" (case-insensitive). Matching the opt-out *phrase* rather than a
 * bare "STOP" means a body like "don't let this deal STOP you" still receives the footer, so every bulk
 * text ships with visible opt-out language (TCPA). Idempotent: the appended footer itself contains
 * "Reply STOP", so a second call is a no-op.
 */
export function appendOptOutFooter(body: string): string {
  if (hasOptOutInstruction(body)) return body;
  return `${body}\n${BULK_SMS_OPT_OUT_FOOTER}`;
}

/**
 * Estimated cost in whole cents for a batch, rounded UP so an estimate never under-reserves against the
 * daily cap. `segmentsPerMessage` is the worst-case (max) rendered segment count across recipients.
 */
export function estimateCostCents(segmentsPerMessage: number, recipients: number): number {
  return Math.ceil(segmentsPerMessage * recipients * SMS_SEGMENT_COST_CENTS);
}
